import { Fragment, ReactNode, useMemo, useState } from 'react'
import { Accordion, Divider, NavLink, Stack, Text } from '@common/ui'
import { matchPath, useLocation, useNavigate } from 'react-router-dom'
import { useCompactNavigation } from './CompactNavigationContext'

export type NavItem = {
  icon?: ReactNode
  text: string
  key: string
  path: string
}

export type NavSection = {
  title: string
  id?: string
  items: readonly NavItem[]
  dividerBefore?: boolean
}

export type NavbarSectionsProps = {
  sections: readonly NavSection[]
}

const getSectionKey = (section: NavSection, index: number) =>
  section.id ?? `${section.title.toLowerCase().replace(/\s+/g, '-')}-${index}`

const NavbarSections = ({ sections }: NavbarSectionsProps) => {
  const { isCompact, closeDrawer } = useCompactNavigation()
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const location = useLocation()
  const navigate = useNavigate()

  const isPathActive = (path: string) =>
    !!matchPath(
      {
        path,
        end: false,
      },
      location.pathname
    )

  const groups = useMemo(() => {
    const visibleSections = sections.filter((section) => section.items.length > 0)
    const result: { key: string; sections: (NavSection & { key: string })[] }[] = []
    visibleSections.forEach((section, index) => {
      const withKey = { ...section, key: getSectionKey(section, index) }
      const startsNewGroup = index === 0 || (section.dividerBefore ?? true)
      if (startsNewGroup || result.length === 0) {
        result.push({ key: `group-${withKey.key}`, sections: [withKey] })
      } else {
        result[result.length - 1].sections.push(withKey)
      }
    })
    return result
  }, [sections])

  const isSectionExpanded = ({ key, items }: NavSection & { key: string }) =>
    expanded[key] ?? items.some(({ path }) => isPathActive(path))

  const handleChange = (groupSections: (NavSection & { key: string })[]) => (value: string[]) => {
    groupSections.forEach((section) => {
      const wasExpanded = isSectionExpanded(section)
      const isExpanded = value.includes(section.key)
      if (wasExpanded !== isExpanded) setExpanded((prev) => ({ ...prev, [section.key]: isExpanded }))
    })
  }

  const handleItemClick = isCompact ? closeDrawer : undefined

  return (
    <Stack gap={0}>
      {groups.map((group, groupIndex) => (
        <Fragment key={group.key}>
          {groupIndex > 0 && <Divider size="md" />}
          <Accordion
            multiple
            value={group.sections.filter(isSectionExpanded).map(({ key }) => key)}
            onChange={handleChange(group.sections)}
            styles={{ content: { padding: 0 } }}
          >
            {group.sections.map((section) => {
              const { title, items, key } = section
              const sectionHasActiveItem = items.some(({ path }) => isPathActive(path))
              return (
                <Accordion.Item value={key} key={key} data-testid={`drawer-section-${key}`}>
                  <Accordion.Control data-testid={`drawer-section-toggle-${key}`}>
                    <Text size="xs" tt="uppercase" fw={600} lts={1.5} c={sectionHasActiveItem ? 'platform' : 'dimmed'}>
                      {title}
                    </Text>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap={0} data-testid={`drawer-section-list-${key}`}>
                      {items.map(({ icon, text, key: itemKey, path }) => (
                        <div key={`item-${itemKey}`} data-testid={`drawer-item-${itemKey}`}>
                          <NavLink
                            href={path}
                            data-testid={`drawer-item-button-${itemKey}`}
                            label={text}
                            leftSection={icon}
                            active={isPathActive(path)}
                            onClick={(event) => {
                              event.preventDefault()
                              navigate(path)
                              handleItemClick?.()
                            }}
                          />
                        </div>
                      ))}
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>
              )
            })}
          </Accordion>
        </Fragment>
      ))}
    </Stack>
  )
}

export default NavbarSections
